import { Link } from "./LocaleLink";
import { BedDouble, Bath, Maximize } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { formatPrice, optimizeImg, localized } from "../lib/api";

export const PropertyCard = ({ property }) => {
  const { t, lang } = useLang();
  const isRental = property.listing_type === "rental";
  const isMid = property.listing_type === "mid_term";
  const badge = isMid ? t("properties.midTerm") : isRental ? t("properties.rental") : t("properties.sale");
  const title = localized(property.title, lang);

  return (
    <Link
      to={`/properties/${property.slug || property.id}`}
      data-testid={`property-card-${property.id}`}
      className="group relative overflow-hidden bg-card border border-border/50 block"
    >
      <div className="aspect-[4/5] md:aspect-square overflow-hidden relative">
        <img
          src={optimizeImg(property.images?.[0], 800)}
          alt={property.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 bg-background/80 backdrop-blur-sm text-primary font-accent text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-primary/40">
          {badge}
        </span>
      </div>

      <div className="p-6 flex flex-col gap-4">
        <div>
          <p className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{property.location}</p>
          <h3 className="font-headings text-2xl leading-snug text-foreground group-hover:text-primary transition-colors duration-300">{title}</h3>
        </div>

        <div className="flex items-center gap-5 text-foreground/70 font-body text-sm">
          <span className="flex items-center gap-1.5"><BedDouble size={16} className="text-primary" /> {property.bedrooms}</span>
          <span className="flex items-center gap-1.5"><Bath size={16} className="text-primary" /> {property.bathrooms}</span>
          <span className="flex items-center gap-1.5"><Maximize size={16} className="text-primary" /> {property.area_sqm} m²</span>
        </div>

        <div className="flex items-end justify-between pt-2 border-t border-border/50">
          <p className="font-body text-xl text-primary" data-testid={`property-price-${property.id}`}>
            {(isRental || isMid) && <span className="text-xs text-muted-foreground mr-1">{t("properties.from")}</span>}
            {formatPrice(property.price, property.price_unit)}
            {isRental && <span className="text-xs text-muted-foreground">{t("properties.perNight")}</span>}
            {isMid && <span className="text-xs text-muted-foreground">{t("properties.perMonth")}</span>}
          </p>
          <span className="font-accent text-[11px] uppercase tracking-[0.2em] text-foreground/60 group-hover:text-primary transition-colors">{t("properties.viewDetails")} →</span>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
